// src/routes/routes.tsx - UPDATED with Payment Routes
import { Navigate, type RouteProps } from "react-router-dom";
import { lazy } from "react";

// Import route modules
import { coreRoutes } from './core-routes';
import { rightOwnerRoutes, rightOwnerNavigationStructure } from './rightowner-routes';
import { licenseeRoutes, licenseeNavigationStructure } from './licensee-routes';
import { adminSystemRoutes, adminNavigationStructure } from './admin-routes';
import { userProfileRoutes, userProfileNavigationStructure } from './user-profile-routes';
import { certificateRoutes, certificateNavigationStructure } from './certificate-routes';
import { profileRoutes } from './profileRoutes';
import { 
  marketplaceRoutes, 
  marketplaceNavigationStructure,
  getMarketplaceRoutesForRole,
  getMarketplaceNavigationForRole,
  getMarketplaceMenuGroups 
} from "./marketplace-routes";
import { 
  paymentRoutes, 
  paymentNavigationStructure,
  getPaymentRoutesForRole,
  getPaymentMenuGroups 
} from "./marketplace/payment-routes";

// Icons for navigation
import {
  FaHome,
  FaChartBar,
  FaStore,
  FaBook,
  FaCloudUploadAlt,
  FaFolder,
  FaCreditCard,
} from "react-icons/fa";
import { CertificateIcons } from '@/components/icons/MenuIcons';
import {
  FiSettings,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { BiCategoryAlt, BiPurchaseTag } from "react-icons/bi";
import {
  MdDashboard,
  MdOutlineAdminPanelSettings,
  MdAddBox,
  MdWarning,
  MdPayment,
} from "react-icons/md";
import {
  HiDocumentSearch,
} from "react-icons/hi";
import {
  RiFilePaperLine,
  RiFileWarningLine,
} from "react-icons/ri";
import { AiOutlineRobot } from "react-icons/ai";

export type RoutesProps = {
  path: RouteProps["path"];
  name: string;
  element: RouteProps["element"];
  exact?: boolean;
  roles?: string[];
  icon?: React.ComponentType<any>;
};

// Combine all routes
export const ROUTES: RoutesProps[] = [
  // Core routes (dashboard, home, auth, etc.)
  ...coreRoutes,
  
  // Feature-specific routes
  ...rightOwnerRoutes,
  ...licenseeRoutes,
  ...adminSystemRoutes,
  ...userProfileRoutes,
  
  // Certificate routes
  ...certificateRoutes,
  
  // Profile routes
  ...profileRoutes,
  
  // Marketplace routes
  ...marketplaceRoutes,
  
  // Payment routes - NEW
  ...paymentRoutes,
];

export const getRoutesByRole = (role: string) => {
  return ROUTES.filter((route) => !route.roles || route.roles.includes(role));
};

// Helper function to get navigation structure by role
export const getNavigationForRole = (userRoles: string[]) => {
  const navigation: any[] = [];

  // Group routes by category for navigation
  const routeGroups = {
    default: {
      title: "navigation.general",
      routes: [
        {
          path: "/dashboard",
          name: "navigation.dashboard",
          icon: MdDashboard,
          roles: [],
        },
        {
          path: "/profile",
          name: "navigation.profile",
          icon: FiUser,
          roles: [],
        },
      ],
    },
    rights: {
      title: "navigation.copyrightManagement",
      routes: [
        {
          path: "/content/create",
          name: "Đăng ký tác phẩm",
          icon: MdAddBox,
          roles: ["right_owner"],
        },
        {
          path: "/catalog/manage",
          name: "Quản lý danh mục",
          icon: BiCategoryAlt,
          roles: ["right_owner"],
        },
        {
          path: "/rights/revenue-dashboard",
          name: "Thống kê doanh thu",
          icon: FaChartBar,
          roles: ["right_owner"],
        },
      ],
    },
    certificates: {
      title: 'navigation.certificates',
      routes: [
        { path: '/certificates/dashboard', name: 'Bảng điều khiển Giấy phép', icon: CertificateIcons.Certificate, roles: ['right_owner', 'admin', 'staff', 'legal'] },
        { path: '/licenses/application', name: 'Yêu cầu cấp phép', icon: CertificateIcons.Application, roles: ['right_owner'] },
        { path: '/licenses/tracking', name: 'Theo dõi tiến độ', icon: CertificateIcons.Tracking, roles: ['right_owner', 'admin', 'staff'] },
        { path: '/licenses/history', name: 'Lịch sử cấp phép', icon: CertificateIcons.History, roles: ['right_owner', 'admin', 'staff'] },
      ],
      subitems: certificateNavigationStructure
    },
    licenses: {
      title: "navigation.licensing",
      routes: [
        {
          path: "/public/works",
          name: "Khám phá tác phẩm",
          icon: FaStore,
          roles: ["licensee", "guest"],
        },
        {
          path: "/licenses/purchase",
          name: "Mua giấy phép",
          icon: BiPurchaseTag,
          roles: ["licensee"],
        },
        {
          path: "/licenses/contracts",
          name: "Hợp đồng hiện tại",
          icon: HiDocumentSearch,
          roles: ["licensee"],
        },
      ],
    },
    certificate: {
      title: "navigation.certificate",
      routes: [],
      subitems: certificateNavigationStructure,
    },
    marketplace: {
      title: "navigation.marketplace",
      routes: [],
      subitems: marketplaceNavigationStructure,
    },
    // NEW: Payment navigation
    payments: {
      title: "navigation.payments",
      routes: [
        {
          path: "/dashboard/payments",
          name: "Quản lý thanh toán",
          icon: FaCreditCard,
          roles: ["right_owner", "licensee", "admin", "staff"],
        },
        {
          path: "/dashboard/payments/installments",
          name: "Thanh toán trả góp",
          icon: MdPayment,
          roles: ["right_owner", "licensee"],
        },
      ],
      subitems: paymentNavigationStructure,
    },
    documents: {
      title: "navigation.documents",
      routes: [
        {
          path: "/documents",
          name: "Thư viện tài liệu",
          icon: FaBook,
          roles: [
            "guest",
            "right_owner",
            "licensee",
            "admin",
            "teacher",
            "student",
          ],
        },
        {
          path: "/documents/upload",
          name: "Tải lên tài liệu",
          icon: FaCloudUploadAlt,
          roles: ["right_owner", "teacher"],
        },
        {
          path: "/documents/my",
          name: "Tài liệu của tôi",
          icon: FaFolder,
          roles: ["right_owner", "licensee", "teacher", "student"],
        },
      ],
    },
    monitoring: {
      title: "navigation.monitoring",
      routes: [
        {
          path: "/monitoring/ai-scan",
          name: "Quét vi phạm AI",
          icon: AiOutlineRobot,
          roles: ["monitor", "right_owner"],
        },
        {
          path: "/monitoring/warnings",
          name: "Cảnh báo vi phạm",
          icon: MdWarning,
          roles: ["monitor"],
        },
      ],
    },
    legal: {
      title: "navigation.legal",
      routes: [
        {
          path: "/legal/disputes",
          name: "Giải quyết tranh chấp",
          icon: RiFilePaperLine,
          roles: ["legal"],
        },
        {
          path: "/legal/violations",
          name: "Xử lý vi phạm",
          icon: RiFileWarningLine,
          roles: ["legal"],
        },
      ],
    },
    admin: {
      title: "navigation.administration",
      routes: [
        {
          path: "/admin/system",
          name: "Cấu hình hệ thống",
          icon: MdOutlineAdminPanelSettings,
          roles: ["admin"],
        },
        {
          path: "/admin/permissions",
          name: "Quản lý người dùng",
          icon: FiUsers,
          roles: ["admin"],
        },
        {
          path: "/admin/payments",
          name: "Quản lý thanh toán",
          icon: FaCreditCard,
          roles: ["admin", "staff"],
        },
      ],
    },
    common: {
      title: "navigation.account",
      routes: [
        {
          path: "/settings",
          name: "navigation.settings",
          icon: FiSettings,
          roles: [],
        },
      ],
    },
  };

  // Filter routes based on user roles
  Object.entries(routeGroups).forEach(([key, group]) => {
    const filteredRoutes = group.routes.filter((route) => {
      if (!route.roles || route.roles.length === 0) return true;
      return route.roles.some((role) => userRoles.includes(role));
    });

    if (filteredRoutes.length > 0) {
      navigation.push({
        ...group,
        routes: filteredRoutes,
      });
    }
  });

  return navigation;
};

// Export navigation structures for easy access
export {
  rightOwnerNavigationStructure,
  licenseeNavigationStructure,
  adminNavigationStructure,
  userProfileNavigationStructure,
  certificateNavigationStructure,
  marketplaceNavigationStructure,
  paymentNavigationStructure, // NEW
};

// Export helper functions
export {
  getMarketplaceRoutesForRole,
  getMarketplaceNavigationForRole,
  getMarketplaceMenuGroups,
  getPaymentRoutesForRole, // NEW
  getPaymentMenuGroups, // NEW
};

// Export routes for easier access
export default ROUTES;